import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import * as SentryCapacitor from "@sentry/capacitor";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { registerSW } from 'virtual:pwa-register';
import { setSWUpdater, nukeAndReload } from '@/lib/swUpdate';
import superjson from "superjson";
import { trpcFetch } from "@/lib/trpcFetch";
import { createAppQueryClient } from "@/lib/appQueryClient";
import App from "./App";
import { SentryUser } from "@/components/SentryUser";
import { getLoginUrl } from "./const";
import { apiUrl, setNativeSessionToken } from "@/lib/apiBase";
import { initKeyboardViewport } from "@/lib/keyboardViewport";
import { initAppViewport } from "@/lib/appViewport";
import { ensureCoachDmNotificationChannel } from "@/lib/coachDmChannel";
import "./index.css";

// Mark the native shell before React paints. Store-review readability rules
// can then target iOS/Android without changing the denser coach web console.
if (Capacitor.isNativePlatform()) {
  const nativePlatform = Capacitor.getPlatform();
  document.documentElement.dataset.nativePlatform = nativePlatform;
  // Fallback for the iOS binary running on Apple silicon. SceneDelegate sets
  // the authoritative marker at document start; this covers older native
  // shells while excluding touch-based iPads that also use a Mac-like UA.
  const isMacDesktopSurface = nativePlatform === 'ios'
    && /^Mac/.test(navigator.platform)
    && navigator.maxTouchPoints === 0;
  if (isMacDesktopSurface) document.documentElement.dataset.macDesktopApp = 'true';
}

// Keep app shells in step with the iOS keyboard (no-op elsewhere).
initAppViewport();
initKeyboardViewport();

// Android: the coach DM channel has to exist before the first message arrives.
// No-op on web and iOS. Does not ask for notification permission.
void ensureCoachDmNotificationChannel();

// Crash reporting — no-ops entirely when VITE_SENTRY_DSN is unset, so the app
// boots normally in dev/local without it. Native (Capacitor) uses the native
// SDK; web/PWA uses the React SDK.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  const sentryOptions = {
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 0.1,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, maskAllInputs: true }),
    ],
  };
  if (Capacitor.isNativePlatform()) {
    SentryCapacitor.init(sentryOptions, Sentry.init);
  } else {
    Sentry.init(sentryOptions);
  }
}

// Web analytics is optional and web-only. Loading it here avoids shipping a
// broken placeholder URL in native builds when analytics variables are absent.
const ANALYTICS_ENDPOINT = import.meta.env.VITE_ANALYTICS_ENDPOINT;
const ANALYTICS_WEBSITE_ID = import.meta.env.VITE_ANALYTICS_WEBSITE_ID;
if (!Capacitor.isNativePlatform() && ANALYTICS_ENDPOINT && ANALYTICS_WEBSITE_ID) {
  const script = document.createElement('script');
  script.defer = true;
  script.src = `${ANALYTICS_ENDPOINT.replace(/\/$/, '')}/umami`;
  script.dataset.websiteId = ANALYTICS_WEBSITE_ID;
  document.head.appendChild(script);
}

async function handleNativeAppUrl(url?: string | null, fromColdLaunch = false) {
  if (!url || !Capacitor.isNativePlatform()) return;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return;
  }
  const isNativeAuth = parsed.protocol === "thestandard:" && parsed.hostname === "auth" && parsed.pathname === "/verify";
  if (!isNativeAuth) {
    const isStandardLink = parsed.protocol === "https:"
      && (parsed.hostname === "setthestandard.fit" || parsed.hostname === "www.setthestandard.fit");
    const isAppRoute = parsed.pathname.startsWith("/client/")
      || parsed.pathname === "/client"
      || parsed.pathname.startsWith("/coach/")
      || parsed.pathname === "/coach"
      || parsed.pathname.startsWith("/groups/join/");
    if (isStandardLink && isAppRoute) {
      const destination = `${parsed.pathname}${parsed.search}${parsed.hash}`;
      // Capacitor can return the same cold-launch URL after the destination
      // reloads. Consume that launch value once so a universal link cannot
      // create a reload loop. Warm appUrlOpen events remain repeatable.
      if (fromColdLaunch) {
        try {
          if (sessionStorage.getItem("native_app_link_handled") === url) return;
          sessionStorage.setItem("native_app_link_handled", url);
        } catch { /* sessionStorage unavailable — navigate once best-effort */ }
      }
      if (`${window.location.pathname}${window.location.search}${window.location.hash}` !== destination) {
        window.location.href = destination;
      }
    }
    return;
  }

  const token = parsed.searchParams.get("token");
  if (!token) {
    window.location.href = "/login?error=missing_token";
    return;
  }

  // Process each launch token exactly once. We navigate with window.location
  // below (a full webview reload), but Capacitor's getLaunchUrl() keeps
  // returning the SAME deep link after every reload — so without this guard the
  // already-consumed token gets re-verified → "token used" error → redirect →
  // reload → getLaunchUrl re-delivers it → infinite flicker on first launch.
  // sessionStorage survives the reloads but clears on a real app restart, which
  // is exactly the scope we want.
  try {
    if (sessionStorage.getItem("native_auth_handled_token") === token) return;
    sessionStorage.setItem("native_auth_handled_token", token);
  } catch { /* sessionStorage unavailable — proceed without dedup */ }

  try {
    const res = await fetch(apiUrl("/api/auth/native-verify"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok || !data?.ok || !data?.sessionToken) {
      window.location.href = `/login?error=${encodeURIComponent(data?.error ?? "invalid_token")}`;
      return;
    }
    setNativeSessionToken(data.sessionToken);
    const destination = data.redirectTo || ((data.role === "admin" || data.role === "coach") ? "/coach/my/today" : "/client");
    window.location.href = destination;
  } catch {
    window.location.href = "/login?error=server_error";
  }
}

if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener("appUrlOpen", ({ url }) => { void handleNativeAppUrl(url, false); });
  CapacitorApp.getLaunchUrl().then((launch) => { void handleNativeAppUrl(launch?.url, true); }).catch(() => {});
}

function CrashFallback({ onReset }: { onReset: () => void }) {
  return (
    <div role="alert" style={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, background: '#0e0e0e', color: '#f5f5f5', textAlign: 'center' }}>
      <p style={{ margin: 0, fontFamily: 'Barlow Condensed, sans-serif', fontSize: 11, fontWeight: 900, letterSpacing: '0.24em', color: '#b28a49', textTransform: 'uppercase' }}>The Standard</p>
      <h1 style={{ margin: 0, fontFamily: 'Barlow Condensed, sans-serif', fontSize: 28, fontWeight: 900, letterSpacing: '0.04em', color: '#ffffff', textTransform: 'uppercase' }}>The app needs a reload</h1>
      <p style={{ margin: 0, fontSize: 14, color: '#9ca3af', maxWidth: 340, lineHeight: 1.6 }}>The app ran into a problem. Reload it now. If it happens again, contact support.</p>
      <button
        onClick={() => { onReset(); window.location.reload(); }}
        style={{ minHeight: 44, padding: '0 28px', borderRadius: 12, background: '#2f5d73', color: '#ffffff', fontFamily: 'Barlow Condensed, sans-serif', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', border: '1px solid #2f5d73' }}
      >
        Reload The Standard
      </button>
      <a href="mailto:felix@fgperformance.com" style={{ fontSize: 13, color: '#71717a', textDecoration: 'underline' }}>Contact support</a>
    </div>
  );
}

// Register the Workbox service worker for full PWA support:
// - Offline caching of app shell and assets
// - Background sync
// - App Store / Play Store install readiness
// SW uses autoUpdate: new versions activate silently in the background.
// This is critical for push notifications — stale SWs silently drop push events.
// The UpdateBanner still handles major version prompts via /api/version polling.
if ('serviceWorker' in navigator) {
  const updateSW = registerSW({
    immediate: true,
    onRegisteredSW(swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      console.log('[PWA] Service worker registered:', swUrl);
      if (!registration) return;
      // iOS standalone PWAs keep the app warm and rarely cold-start, so a
      // new build is never detected unless we actively poll for the new
      // SW — both on an interval and whenever the app returns to the
      // foreground (resume from the home screen).
      const checkForUpdate = () => { registration.update().catch(() => {}); };
      setInterval(checkForUpdate, 5 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate();
      });
    },
    onRegisterError(error: unknown) {
      console.warn('[PWA] Service worker registration failed:', error);
    },
  });
  // Let the UpdateBanner activate the waiting SW + reload under it,
  // instead of a bare reload the stale SW would intercept on iOS.
  setSWUpdater(updateSW);
}

// Lazy-route chunk failed to load — almost always a stale SW serving an old
// index after a deploy, so a route's /assets/<hash>.js is gone. Vite fires this
// on a failed dynamic import. Recover via the hardened nuke-and-reload instead
// of leaving the user on a blank route. Guarded (shared key with the inline
// index.html recovery) so we never loop on a genuinely missing chunk.
window.addEventListener('vite:preloadError', () => {
  try {
    const last = parseInt(sessionStorage.getItem('__appRecoverAt') || '0', 10);
    if (Date.now() - last < 30000) return;
    sessionStorage.setItem('__appRecoverAt', String(Date.now()));
  } catch { /* sessionStorage unavailable — fall through and recover anyway */ }
  void nukeAndReload();
});

// Sane defaults for a mobile-first premium app: keep data fresh for 1 minute
// without refetching on every tab focus (was killing battery on Capacitor),
// hold cached data for 30 minutes after unmount, and retry failed queries
// once rather than the default 3 (fail fast, surface the error).
const queryClient = createAppQueryClient();

// ── Live data on app resume ──────────────────────────────────────────────────
// refetchOnWindowFocus is globally off (battery), and interval polls are
// suspended while the app is backgrounded — so without this, DM threads and
// unread/alert badges stayed frozen after returning to the app until the user
// manually refreshed. On every return to the foreground, refetch the active
// conversational and cross-device training queries immediately.
// visibilitychange fires in WKWebView on native resume, so this covers
// iOS/Android Capacitor and the web alike. The watch writes through the server,
// so its workout/activity credit must invalidate the phone's cached feed.
const LIVE_QUERY_FAMILIES = new Set([
  'messages', 'notifications', 'typing', 'quickActivity', 'workouts',
]);
const refetchLiveQueries = () => {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const head = Array.isArray(query.queryKey) ? (query.queryKey as unknown[])[0] : undefined;
      const family = Array.isArray(head) ? String((head as string[])[0]) : undefined;
      return !!family && LIVE_QUERY_FAMILIES.has(family);
    },
  });
};
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refetchLiveQueries();
  });
}
if (typeof window !== 'undefined') {
  window.addEventListener('online', refetchLiveQueries);
}

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  window.location.href = getLoginUrl();
};

// Capture every tRPC failure to Sentry, tagged with the procedure path (derived
// from the react-query key, e.g. [['messages','inbox'], …] → "messages.inbox").
// Skips auth redirects (expected). No-ops when Sentry isn't initialised.
const trpcPathFromKey = (key: unknown): string | undefined => {
  const head = Array.isArray(key) ? (key as unknown[])[0] : undefined;
  return Array.isArray(head) ? (head as string[]).join('.') : undefined;
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
    if (error && !(error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG)) {
      Sentry.captureException(error, { tags: { path: trpcPathFromKey(event.query.queryKey), kind: "query" } });
    }
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "success") {
    const path = trpcPathFromKey(event.mutation.options.mutationKey);
    if (/^(workouts|checkins|nutrition|devotional|habits|journal|health)\./.test(path ?? '')) {
      void queryClient.invalidateQueries({ queryKey: [["gamification"]] });
    }
  }
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
    if (error && !(error instanceof TRPCClientError && error.message === UNAUTHED_ERR_MSG)) {
      Sentry.captureException(error, { tags: { path: trpcPathFromKey(event.mutation.options.mutationKey), kind: "mutation" } });
    }
  }
});

async function boot() {
  const reviewFixture = import.meta.env.DEV && window.location.pathname.startsWith('/__dev/ipad-desktop');
  const link = reviewFixture
    ? (await import('./dev/ipadDesktopFixture')).ipadDesktopFixtureLink()
    : httpBatchLink({
        url: apiUrl("/api/trpc"),
        transformer: superjson,
        fetch: trpcFetch,
      });
  const trpcClient = trpc.createClient({ links: [link] });

  createRoot(document.getElementById("root")!).render(
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <Sentry.ErrorBoundary fallback={({ resetError }) => <CrashFallback onReset={resetError} />}>
          <SentryUser />
          <App />
        </Sentry.ErrorBoundary>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

void boot();
